import { Box } from "@mui/material";
import Text from "../../atoms/Text/Text";
import { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { config as appConfig } from "../../../config";
import { isAppSource } from "../../../helpers/getSourceFromUrl";

import { useGetMeanQuery, usePostChangeMeanMutation } from "../../../rtk/mean/mean";
import { ChangeMeanRequest } from "../../../types/rtk/mean/mean";
import { useNavigateWithParams } from "../../../hooks/useNavigateWithParams";
import { AppRoutes } from "../../../routes/AppRoutes";
import SpinnerLoader from "../../atoms/SpinnerLoader/SpinnerLoader";

import PaymentMean from "../../organisms/PaymentMean/PaymentMean";
import Header from "../../templates/Header/Header";

import PaymentHistoryList from "../../organisms/PaymentsHistoryList/PaymentsHistoryList";
import { useGetPaymentHistoryQuery } from "../../../rtk/paymentsHistory/paymentsHistory";


const ManagePayments = () => {
  const { t } = useTranslation();

  const [isRedirecting, setIsRedirecting] = useState(false);

  const navigate = useNavigateWithParams();
  const searchParams = new URLSearchParams(window.location.search);

  // "success_delay" is appended by handleChangeMeanRedirect after a successful Stripe redirect,
  // to give the backend time to process the new payment mean before we fetch it.
  const meanDelayParam = searchParams.get("success_delay");

  // "origin" indicates where the user came from, to know where to redirect after the flow.
  // for now, it's only used to redirect back to the subscription management page after changing the mean from there, but it could be useful for other flows in the future.
  const originParam = searchParams.get("origin");

  const meanDelayMs = meanDelayParam !== null ? parseInt(meanDelayParam, 10) : 0;
  const [isMeanDelayed, setIsMeanDelayed] = useState(meanDelayMs > 0);

  // If the user came from the subscription page, redirect back to it immediately on mount.
  useEffect(() => {
    if (meanDelayParam !== null && originParam === "manage-subscriptions") {
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS);
    }
  }, [meanDelayParam, originParam, navigate]);

  // Wait for the delay before fetching the updated mean data.
  useEffect(() => {
    if (meanDelayMs > 0) {
      const timer = setTimeout(() => setIsMeanDelayed(false), meanDelayMs);
      return () => clearTimeout(timer);
    }
  }, [meanDelayMs]);

  const { data: meanData, isLoading: meanLoading, error: meanError } = useGetMeanQuery(undefined, { skip: isMeanDelayed });
  const [changeMean, { isLoading: changeMeanLoading }] = usePostChangeMeanMutation();

  const handleChangeMeanRedirect = async () => {
    setIsRedirecting(true);
    const query_params = window.location.search;
    const changeMeanRequest : ChangeMeanRequest = {
      base_url: window.location.origin,
      query_params: `${query_params}${query_params ? "&" : "?"}success_delay=3000`,
    };
    const data = await changeMean(changeMeanRequest).unwrap();
    if (data?.checkout_url) {
      window.location.href = data.checkout_url;
    }
    else {
      setIsRedirecting(false);
    }
  };

  const { data: paymentHistoryData, isLoading: paymentHistoryLoading, error: paymentHistoryError } = useGetPaymentHistoryQuery();

  return (
    <>
      {!isAppSource() && (
        <Header
          headerStep={{ context: "manage" }}
          title={t("subscription:MANAGE_MEAN.TITLE")}
          goBack={() => window.location.href = appConfig.REACT_APP_FE_BASE_URL}
        />
      )}
      <Box className="ManagePayments">

        {(meanLoading || isMeanDelayed || paymentHistoryLoading) && <SpinnerLoader />}

        {!meanLoading && meanError && (
          <Text children={"Impossible to load payment mean"} />
        )}

        {!meanLoading && meanData && (
          <PaymentMean
            data={meanData}
            onChangeMean={handleChangeMeanRedirect}
            isLoading={changeMeanLoading || isRedirecting}
          />
        )}

        {!paymentHistoryLoading && paymentHistoryError && (
          <Text children={"Impossible to load payment history"} />
        )}

        {!paymentHistoryLoading && paymentHistoryData && (
              <PaymentHistoryList data={paymentHistoryData}/>
        )}

      </Box>
    </>
  );
};

export default ManagePayments;